import Form from "react-bootstrap/Form";
import "./addapprovalworkflow.scss";
import { Button } from "react-bootstrap";
import { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import useApprovalWorkflow from "../../../Hooks/useApprovalWorkflow";
import useUser from "../../../Hooks/useUser";
import { useAppSelector } from "../../../redux/hooks";
import { selectCurrentUserId } from "../../../redux/auth/authSlice";
import * as Yup from "yup";
import { FieldArray, FormikHelpers, FormikProvider, useFormik } from "formik";
import Loading from "../../../components/LoadingPage/Loading";
import {
  PermissionIdentifier,
  PermissionModule,
  useModulePermissions,
} from "../../../utils/permissionUtils";
import { ApprovalMode } from "../../../redux/approvalWorkflow/approvalWorkflowSlice";
import { toast } from "react-toastify";

interface UserOption {
  id: string;
  userName: string;
  email: string;
  role?: { roleName?: string };
}

interface Approver {
  id: string;
  userId: string;
  userName: string;
  email: string;
  approvalOrder: number | "";
}

const AddApprovalWorkflow = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();
  const {
    editApprovalWorkflowAPI,
    editApprovalWorkflowLoading,
    getApprovalWorkflowByIdAPI,
    getApprovalWorkflowByIdLoading,
    getApprovalModesAPI,
    getApprovalModesLoading,
    postApprovalWorkflowAPI,
    postApprovalWorkflowLoading,
    postApproverDetailAPI,
    postApproverDetailLoading,
    putApproverDetailAPI,
    putApproverDetailLoading,
  } = useApprovalWorkflow();

  const { getUserList } = useUser();

  const [isEdit, setIsEdit] = useState(false);
  const currentUserId = useAppSelector(selectCurrentUserId);
  const [approvalModes, setApprovalModes] = useState<ApprovalMode[]>([]);
  const [userOptions, setUserOptions] = useState<UserOption[]>([]);
  const emptyApprover: Approver = { id: "", userId: "", userName: "", email: "", approvalOrder: "" };
  const [dynamicVal, setDynamicVal] = useState({
    moduleName: "",
    workflowName: "",
    approvalModeId: "",
    approverDetails: [{ ...emptyApprover }] as Approver[],
    isActive: true,
    createdBy: currentUserId,
  });

  useEffect(() => {
    loadApprovalModes();
    loadUsers();
    if (location.pathname.includes("/edit-approval-workflow")) {
      setIsEdit(true);
      if (id) {
        loadWorkflowData(id);
      } else {
        navigate("/approval-workflow-master");
      }
    }
  }, []);

  const loadApprovalModes = () => {
    getApprovalModesAPI()
      .then((res: any) => setApprovalModes(res || []))
      .catch();
  };

  const loadUsers = () => {
    getUserList({})
      .then((res: any) => {
        if (res?.data) {
          setUserOptions(res.data);
        } else if (Array.isArray(res)) {
          setUserOptions(res);
        }
      })
      .catch();
  };

  const loadWorkflowData = (workflowId: string) => {
    getApprovalWorkflowByIdAPI(workflowId)
      .then((res: any) => {
        if (res) {
          setDynamicVal({
            moduleName: res.moduleName || "",
            workflowName: res.workflowName || "",
            approvalModeId: res.approvalModeId || "",
            approverDetails: Array.isArray(res.approverDetails) && res.approverDetails.length > 0
              ? res.approverDetails.map((a: any) => ({
                id: a.id || "",
                userId: a.approverUserId || "",
                userName: a.user?.userName || "",
                email: a.user?.email || "",
                approvalOrder: a.approvalOrder || "",
              }))
              : [{ ...emptyApprover }],
            isActive: res.isActive ?? true,
            createdBy: res.createdBy || currentUserId,
          });
        }
      })
      .catch(() => navigate("/approval-workflow-master"));
  };

  const formik = useFormik({
    initialValues: dynamicVal,
    validationSchema: Yup.object({
      moduleName: Yup.string().required("Module Name is required"),
      workflowName: Yup.string().required("Workflow Name is required"),
      approverDetails: Yup.array().of(
        Yup.object({
          userId: Yup.string().required("Please select a user"),
          approvalOrder: Yup.number()
            .typeError("Must be a number")
            .required("Order is required")
            .min(1, "Min order is 1"),
        })
      ),
    }),
    onSubmit: async (
      values: any,
      { setSubmitting }: FormikHelpers<any>
    ) => {
      try {
        const { approverDetails, ...workflowData } = values;
        const validApprovers = approverDetails.filter(
          (a: Approver) => a.userId && a.approvalOrder
        );

        if (isEdit) {
          await editApprovalWorkflowAPI({ ...workflowData, id, updatedBy: currentUserId });
          if (validApprovers.length > 0) {
            const existingApprovers = validApprovers.filter((a: Approver) => a.id);
            const newApprovers = validApprovers.filter((a: Approver) => !a.id);

            await Promise.all([
              ...existingApprovers.map((approver: Approver) =>
                putApproverDetailAPI({
                  id: approver.id,
                  isActive: true,
                  updatedBy: currentUserId,
                  approvalWorkflowId: id,
                  approverUserId: approver.userId,
                  approvalOrder: approver.approvalOrder,
                })
              ),
              ...newApprovers.map((approver: Approver) =>
                postApproverDetailAPI({
                  isActive: true,
                  createdBy: currentUserId,
                  approvalWorkflowId: id,
                  approverUserId: approver.userId,
                  approvalOrder: approver.approvalOrder,
                })
              ),
            ]);
          }
          toast.success("Success! Approval Workflow has been Updated.");
        } else {
          const workflowRes = await postApprovalWorkflowAPI(workflowData);
          const approvalWorkflowId = workflowRes?.id;

          if (approvalWorkflowId && validApprovers.length > 0) {
            await Promise.all(
              validApprovers.map((approver: Approver) =>
                postApproverDetailAPI({
                  isActive: true,
                  createdBy: currentUserId,
                  approvalWorkflowId,
                  approverUserId: approver.userId,
                  approvalOrder: approver.approvalOrder,
                })
              )
            );
          }
          toast.success("Success! Approval Workflow has been Created.");
        }
        navigate("/approval-workflow-master");
      } catch (err) { }
      setSubmitting(false);
    },
    enableReinitialize: true,
  });

  const { handleChange, values, handleSubmit, touched, errors } = formik;

  const isPermitted = useModulePermissions(PermissionModule.APPROVAL_WORKFLOW);
  const isAddPermitted = isPermitted(
    PermissionIdentifier.CREATE_APPROVAL_WORKFLOW
  );
  const isEditPermitted = isPermitted(
    PermissionIdentifier.EDIT_APPROVAL_WORKFLOW
  );

  const isLoading =
    postApprovalWorkflowLoading ||
    postApproverDetailLoading ||
    putApproverDetailLoading ||
    editApprovalWorkflowLoading ||
    getApprovalWorkflowByIdLoading ||
    getApprovalModesLoading;

  return (
    <div className="add-approval-workflow-wrapper">
      {isLoading && <Loading />}
      <div className="page-title">
        <h2>{isEdit ? "Edit" : "Add"} Approval Workflow</h2>
      </div>
      <div className="add-approval-workflow-box">
        <Form onSubmit={handleSubmit}>
          <Form.Group className="mb-4">
            <Form.Label>
              Module Name<sup>*</sup>
            </Form.Label>
            <Form.Control
              type="text"
              placeholder="Enter Module Name"
              id="moduleName"
              name="moduleName"
              onChange={handleChange}
              value={values.moduleName}
              isInvalid={touched.moduleName && !!errors.moduleName}
            />
            {touched.moduleName && errors.moduleName ? (
              <Form.Control.Feedback type="invalid">
                {errors.moduleName}
              </Form.Control.Feedback>
            ) : null}
          </Form.Group>
          <Form.Group className="mb-4">
            <Form.Label>
              Workflow Name<sup>*</sup>
            </Form.Label>
            <Form.Control
              type="text"
              placeholder="Enter Workflow Name"
              id="workflowName"
              name="workflowName"
              onChange={handleChange}
              value={values.workflowName}
              isInvalid={touched.workflowName && !!errors.workflowName}
            />
            {touched.workflowName && errors.workflowName ? (
              <Form.Control.Feedback type="invalid">
                {errors.workflowName}
              </Form.Control.Feedback>
            ) : null}
          </Form.Group>
          <Form.Group className="mb-4">
            <Form.Label>Approval Mode</Form.Label>
            <Form.Select
              id="approvalModeId"
              name="approvalModeId"
              onChange={handleChange}
              value={values.approvalModeId}
            >
              <option value="">Select Approval Mode</option>
              {approvalModes.map((mode) => (
                <option key={mode.id} value={mode.id}>
                  {mode.mode}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
          <div className="mb-4 full-width approver-details-section">
            <div className="approver-details-header">
              <Form.Label>Approver Details</Form.Label>
            </div>
            <FormikProvider value={formik}>
              <FieldArray name="approverDetails">
                {({ push, remove }) => (
                  <>
                    {values.approverDetails.map((_: Approver, index: number) => {
                      const approverErrors = (errors.approverDetails as any)?.[index] || {};
                      const approverTouched = (touched.approverDetails as any)?.[index] || {};
                      return (
                        <div className="approver-row" key={index}>
                          <Form.Group>
                            <Form.Label>User<sup>*</sup></Form.Label>
                            <Form.Select
                              name={`approverDetails.${index}.userId`}
                              value={values.approverDetails[index].userId}
                              isInvalid={approverTouched.userId && !!approverErrors.userId}
                              onChange={(e) => {
                                const selectedUser = userOptions.find(
                                  (u) => u.id === e.target.value
                                );
                                formik.setFieldValue(`approverDetails.${index}.userId`, e.target.value);
                                formik.setFieldValue(`approverDetails.${index}.userName`, selectedUser?.userName || "");
                                formik.setFieldValue(`approverDetails.${index}.email`, selectedUser?.email || "");
                              }}
                            >
                              <option value="">Select User</option>
                              {userOptions
                                .filter((user) => {
                                  const selectedIds = values.approverDetails
                                    .map((a: Approver, i: number) => i !== index ? a.userId : null)
                                    .filter(Boolean);
                                  return !selectedIds.includes(user.id) && user.role?.roleName?.toLowerCase() !== "sysadmin";
                                })
                                .map((user) => (
                                  <option key={user.id} value={user.id}>
                                    {user.userName} - {user.email}
                                  </option>
                                ))}
                            </Form.Select>
                            <Form.Control.Feedback type="invalid">
                              {approverErrors.userId}
                            </Form.Control.Feedback>
                          </Form.Group>
                          <Form.Group>
                            <Form.Label>Approval Order<sup>*</sup></Form.Label>
                            <Form.Control
                              type="number"
                              placeholder="Order"
                              min={1}
                              name={`approverDetails.${index}.approvalOrder`}
                              onChange={handleChange}
                              value={values.approverDetails[index].approvalOrder}
                              isInvalid={approverTouched.approvalOrder && !!approverErrors.approvalOrder}
                            />
                            <Form.Control.Feedback type="invalid">
                              {approverErrors.approvalOrder}
                            </Form.Control.Feedback>
                          </Form.Group>
                          <div className="approver-row-actions">
                            {values.approverDetails.length > 1 && (
                              <Button
                                variant="outline-danger"
                                size="sm"
                                onClick={() => remove(index)}
                              >
                                Remove
                              </Button>
                            )}
                          </div>
                        </div>
                      );
                    })}
                    <div className="approver-add-btn">
                      <Button
                        variant="outline-primary"
                        size="sm"
                        onClick={() => push({ ...emptyApprover })}
                      >
                        + Add Approver
                      </Button>
                    </div>
                  </>
                )}
              </FieldArray>
            </FormikProvider>
          </div>
          <Form.Group className="mb-4">
            <div className="item-checkbox-content">
              <Form.Check
                inline
                label="Active"
                type="checkbox"
                id="isActive"
                name="isActive"
                onChange={handleChange}
                checked={values.isActive}
              />
            </div>
          </Form.Group>
          <Form.Group className="mb-4 full-width text-end">
            <Button
              style={{ marginRight: "10px" }}
              variant="outline-primary"
              onClick={() => navigate("/approval-workflow-master")}
            >
              Cancel
            </Button>
            <Button
              type="submit"
            // disabled={!isAddPermitted && !isEditPermitted}
            >
              {isEdit ? "Update" : "Add"}
            </Button>
          </Form.Group>
        </Form>
      </div>
    </div>
  );
};

export default AddApprovalWorkflow;
